import React, { useState } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import { CustomerSettingsState } from "../../settings/models/models";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../shared/themes/redesignTokens";
import InvitePartnerDialog from "./InvitePartnerDialog";

interface InvitePartnerButtonProps {
  variant?: "text" | "contained" | "icon";
  size?: "small" | "medium" | "large";
  label?: string;
  onSuccess?: (email?: string, companyName?: string) => void;
}

const InvitePartnerButton: React.FC<InvitePartnerButtonProps> = ({
  variant = "contained",
  size = "medium",
  label,
  onSuccess,
}) => {
  const { translate } = useTranslation("partnerships");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { customer: ownCustomer } = useAppSelector<CustomerSettingsState>(
    (state: any) => state.customerSettings
  );

  const buttonLabel = label || translate("partnerships:invite.button");
  const shortLabel = translate("partnerships:invite.buttonShort");

  if (variant === "icon") {
    return (
      <>
        <Tooltip title={buttonLabel} arrow>
          <IconButton
            onClick={() => setDialogOpen(true)}
            size={size}
            sx={{
              color: dt.blue,
              "&:hover": {
                backgroundColor: dt.blueGlow,
              },
            }}
          >
            <PersonAddOutlinedIcon sx={{ fontSize: size === "small" ? "18px" : "22px" }} />
          </IconButton>
        </Tooltip>

        <InvitePartnerDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={onSuccess}
          viewerCustomerType={ownCustomer?.customerType}
        />
      </>
    );
  }

  if (variant === "text") {
    return (
      <>
        <Button
          onClick={() => setDialogOpen(true)}
          size={size}
          startIcon={<PersonAddOutlinedIcon sx={{ fontSize: "16px !important" }} />}
          sx={{
            fontFamily: dt.font,
            fontSize: size === "small" ? "12px" : "13px",
            fontWeight: 600,
            textTransform: "none",
            color: dt.blue,
            borderRadius: dt.radiusSm,
            "&:hover": {
              backgroundColor: dt.blueSoft,
            },
          }}
        >
          {size === "small" ? shortLabel : buttonLabel}
        </Button>

        <InvitePartnerDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={onSuccess}
          viewerCustomerType={ownCustomer?.customerType}
        />
      </>
    );
  }

  // contained variant (default)
  return (
    <>
      <Button
        onClick={() => setDialogOpen(true)}
        variant="contained"
        size={size}
        startIcon={<PersonAddOutlinedIcon sx={{ fontSize: "16px !important" }} />}
        sx={{
          fontFamily: dt.font,
          background: accentGradientDark,
          borderRadius: dt.radiusPill,
          padding: size === "small" ? "7px 16px" : { xs: "8px 18px", md: "11px 30px" },
          textTransform: "none",
          fontWeight: 600,
          fontSize: size === "small" ? "12px" : { xs: "13px", md: "14px" },
          boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
          whiteSpace: "nowrap",
          flexShrink: 0,
          "&:hover": {
            background: accentGradientDarkHover,
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(52,152,219,0.3)",
          },
        }}
      >
        {size === "small" ? shortLabel : buttonLabel}
      </Button>

      <InvitePartnerDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={onSuccess}
        viewerCustomerType={ownCustomer?.customerType}
      />
    </>
  );
};

export default InvitePartnerButton;
